import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { EDocumentState } from "../eDocumentSlides";
import { toNumberFormat } from "@utils/Utils";
import { ON_INVOICE_DISCOUNT_TYPE_CODE } from "@utils/Constants";
import { StoredOtherCharge, totalOtherCharges } from "@utils/DocumentOtherCharges";

export const updateCreditNoteDataState = (state: EDocumentState) => {
  const { documentProducts = [], documentNoteTotal = 0 } = state
  let descuentoTotalNota = new Number(documentNoteTotal || 0).valueOf();
  const unSelectedLines = documentProducts.filter(p => p.selected != "true")
  const selectedLines = documentProducts.filter(p => p.selected == "true")

  unSelectedLines.forEach(p => {
    p.cant = 0;
    p.price = 0;
    p.impuestos = 0;
    p.impuestos_servicio = 0;
    p.descuento = 0;
    p.subtotal = 0;
  })

  updateCreditnoteProducts(descuentoTotalNota, selectedLines)
}

export const createDiscountCreditNoteProducts = (documentNoteTotal: number, productLines: EDocumentProductDataType[]) => {
  updateCreditnoteProducts(documentNoteTotal, productLines)
}

const updateCreditnoteProducts = (creditNoteTotal: number, selectedLines: EDocumentProductDataType[]): void => {
  const totalSelectedLines = getSelectProductTotal(selectedLines)
  let porcentajeDescuento = ((creditNoteTotal * 100) / Number(totalSelectedLines) / 100);

  selectedLines.forEach(p => {
    const { cantFact: invoiceCant, priceFact, porcentaje_impuesto: porcentajeImpuesto,
      codigo_cabys: cabys
    } = p;

    const formatPrice = Number(priceFact || 0)
    const formatCant = Number(invoiceCant || 0)
    const formatPorcentajeImpuestos = Number(porcentajeImpuesto || 0)

    let precioUnitarioDescuento = 0;
    let precioLineaDescuento;
    let impuestos;

    precioUnitarioDescuento = Number(toNumberFormat(formatPrice * porcentajeDescuento));
    precioLineaDescuento = formatCant * precioUnitarioDescuento;
    impuestos = Number(toNumberFormat(precioLineaDescuento * ((formatPorcentajeImpuestos / 100))));

    p.description = `Descuento sobre ${p.descriptionFact}`;
    p.codigo_cabys = cabys;
    p.cant = invoiceCant;
    p.price = precioUnitarioDescuento;
    p.impuestos = impuestos;
    p.impuestos_servicio = 0;
    p.descuento = 0;
    p.subtotal = Number(toNumberFormat(precioLineaDescuento));
    p.porcentaje_impuesto = porcentajeImpuesto;
  });
}

export const getSelectProductTotal = (products: EDocumentProductDataType[]): Number => {
  return products.filter(p => p.selected == "true").reduce((a, p) => {
    let impuestos = Number(p.impuestosFact || 0);
    let descuento = Number(p.descuentoFact || 0);
    var invoiceCant = Number(p.cantFact || 0);
    let precio = Number(p.priceFact || 0);
    return a + ((invoiceCant * precio) + impuestos - descuento);
  }, Number(0));
}

/**
 * Extras opcionales del resumen. Es un objeto y no un parametro posicional a
 * proposito: el plan de "TotalComprobante neto de IVA Devuelto" agregara aqui
 * `paymentWays` sin tener que volver a tocar los call sites.
 */
export type DocumentGranTotalsExtras = {
  otherCharges?: StoredOtherCharge[];
};

/**
 * Orden de calculo fijo, espejo de lo que hace la lambda al armar el resumen
 * (`TotalComprobante = TotalVentaNeta + TotalImpuesto + TotalOtrosCargos - TotalIVADevuelto`):
 *
 *   1. base = subtotal - descuento   (solo lineas)
 *   2. otrosCargos = total de los cargos resueltos sobre esa base
 *   3. ivaDevuelto (cuando exista) sale de las lineas de salud + monto en tarjeta
 *      y NUNCA ve los otros cargos
 *   4. total = lineas + otrosCargos - ivaDevuelto
 *
 * Dos invariantes evitan el calculo circular: la base del porcentaje nunca
 * incluye otros cargos (no hay cargo sobre cargo) y el IVA devuelto nunca los ve.
 */
export const calculateDocumentGranTotals = (
  products: EDocumentProductDataType[],
  documentDiscountType: string = ON_INVOICE_DISCOUNT_TYPE_CODE,
  extras?: DocumentGranTotalsExtras,
) => {
  const documentSummary = products
    .filter(p => !p.toDelete).
    reduce((a, p) => {
      const subtotal = Number(p.subtotal || 0);
      const impuestos = Number(p.impuestos || 0);
      let impuestosExentos = Number(0);
      if (p.exemption) {
        impuestosExentos = Number(p.exemption.monto_impuesto || 0);
      }
      const descuento = Number(p.descuento || 0);

      a.subtotal += subtotal;
      a.impuestos += impuestos - impuestosExentos;
      a.descuento += descuento;
      a.impuestosExentos += impuestosExentos;

      if (documentDiscountType === ON_INVOICE_DISCOUNT_TYPE_CODE) {
        a.total += subtotal + impuestos - descuento - impuestosExentos;
      } else {
        a.total += subtotal + impuestos - impuestosExentos;
      }
      return a;
    }, {
      subtotal: Number(0),
      descuento: Number(0),
      impuestos: Number(0),
      impuestosExentos: Number(0),
      total: Number(0),
      otrosCargos: Number(0)
    })

  // Con CREDIT_NOTE_DISCOUNT_TYPE_CODE el descuento no se resta del total ni viaja
  // al comprobante (sale despues como nota aparte), asi que el TotalVentaNeta
  // firmado es el bruto y esa es la base correcta del porcentaje.
  const ventaNeta = documentDiscountType === ON_INVOICE_DISCOUNT_TYPE_CODE
    ? documentSummary.subtotal - documentSummary.descuento
    : documentSummary.subtotal;

  documentSummary.otrosCargos = totalOtherCharges(extras?.otherCharges, ventaNeta)
  documentSummary.total += documentSummary.otrosCargos

  return documentSummary
}